import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { Volume2, VolumeX, RotateCw, Share2 } from "lucide-react";
import ShareCard from "@/components/ShareCard";

/**
 * nuvola — Doodle Jump style game with cinematic monochrome cloudscape.
 * - Parallax tiled sky background (sky_tile.jpg) scrolling slowly with the camera.
 * - Distant haze clouds drifting horizontally.
 * - Mid-distance cloud sprites (cloud_distant.png) for atmosphere.
 * - Foreground platforms rendered as cloud_puff.png sprites.
 * - Wooden mascot (mascot.png, transparent) as the player.
 */
export default function NuvolaGame({ email, audioOn, toggleAudio, apiBase, onExitToLanding }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const stateRef = useRef(null);
  const rafRef = useRef(0);
  const assetsRef = useRef({ ready: 0, total: 4 });
  const [score, setScore] = useState(0);
  const [bestScore, setBestScore] = useState(() => {
    // Quick local hint until the server returns the authoritative personal best.
    const v = parseInt(localStorage.getItem("nuvola_best") || "0", 10);
    return isNaN(v) ? 0 : v;
  });
  const [worldBest, setWorldBest] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const [assetsReady, setAssetsReady] = useState(false);
  const [showShare, setShowShare] = useState(false);

  // Preload all sprites
  useEffect(() => {
    const sources = {
      sky: "/assets/game_bg.jpg",
      cloud: "/assets/moon_platform.png",
      mascot: "/assets/puppet.png",
      mascotL: "/assets/puppet_left.png",
      mascotR: "/assets/puppet_right.png",
      mark: "/assets/nuvola_w.png",
    };
    const imgs = {};
    let loaded = 0;
    Object.entries(sources).forEach(([k, src]) => {
      const im = new Image();
      im.onload = () => {
        loaded += 1;
        imgs[k] = im;
        if (loaded === Object.keys(sources).length) {
          assetsRef.current = { ...imgs, ready: loaded, total: loaded };
          setAssetsReady(true);
        }
      };
      im.onerror = () => {
        loaded += 1;
        if (loaded === Object.keys(sources).length) {
          assetsRef.current = { ...imgs, ready: loaded, total: loaded };
          setAssetsReady(true);
        }
      };
      im.src = src;
      imgs[k] = im;
    });
  }, []);

  const initState = useCallback((w, h) => {
    // Scale all sizes relative to viewport width so mobile feels proportional
    const isMobile = w < 640;
    const scale = isMobile ? 0.55 : 1.0;
    let platformIdCounter = 0;
    const platforms = [];
    // First (landing) cloud — directly under mascot
    const firstPw = 220 * scale;
    platforms.push({ id: platformIdCounter++, x: w / 2 - firstPw / 2, y: h - 150, w: firstPw, h: 60 * scale, drift: 0, scale: 1.0 });
    // Sparse platforms — fewer means harder
    const COUNT = 8;
    const verticalGap = (h / 6.5);
    for (let i = 1; i < COUNT; i++) {
      const pw = (130 + Math.random() * 70) * scale;
      platforms.push({
        id: platformIdCounter++,
        x: Math.random() * (w - pw - 20) + 10,
        y: h - 150 - i * verticalGap,
        w: pw,
        h: (48 + Math.random() * 12) * scale,
        drift: (Math.random() - 0.5) * 0.55,
        scale: 0.9 + Math.random() * 0.3,
      });
    }
    const distantClouds = Array.from({ length: 4 }, () => ({
      x: Math.random() * w,
      y: Math.random() * h * 1.4,
      w: 360 + Math.random() * 220,
      vx: (Math.random() * 0.18 + 0.03) * (Math.random() < 0.5 ? -1 : 1),
      alpha: 0.10 + Math.random() * 0.10,
      depth: 0.15 + Math.random() * 0.25,
    }));
    return {
      w, h,
      isMobile,
      player: {
        x: w / 2 - 28 * scale,
        y: -140,
        vx: 0,
        vy: 0,
        w: 56 * scale,
        h: 86 * scale,
        tilt: 0,
      },
      gravity: 0.34,
      jumpV: -13.6 * Math.sqrt(scale),
      moveAccel: 0.65,
      friction: 0.86,
      maxVx: 6.6,
      keys: { left: false, right: false },
      targetX: null,           // mobile slider: target X position (or null)
      platforms,
      distantClouds,
      cameraY: 0,
      bgOffset: 0,
      score: 0,
      lastPlatformId: null,
      platformIdCounter,
      dead: false,
      time: 0,
      hasLanded: false,
    };
  }, []);

  // Setup canvas + loop
  useEffect(() => {
    if (!assetsReady) return;
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      const w = cv.clientWidth;
      const h = cv.clientHeight;
      cv.width = w * dpr;
      cv.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (!stateRef.current) stateRef.current = initState(w, h);
      else { stateRef.current.w = w; stateRef.current.h = h; }
    };
    resize();
    window.addEventListener("resize", resize);

    const onKey = (e, down) => {
      const s = stateRef.current;
      if (!s) return;
      if (e.key === "ArrowLeft" || e.key === "a" || e.key === "A") s.keys.left = down;
      if (e.key === "ArrowRight" || e.key === "d" || e.key === "D") s.keys.right = down;
      if (down && (e.key === "ArrowLeft" || e.key === "ArrowRight" || e.key === " " || e.key === "a" || e.key === "d" || e.key === "A" || e.key === "D")) {
        e.preventDefault();
        setShowIntro(false);
      }
    };
    const onDown = (e) => onKey(e, true);
    const onUp = (e) => onKey(e, false);
    window.addEventListener("keydown", onDown);
    window.addEventListener("keyup", onUp);

    const onMouseMove = (e) => {
      const s = stateRef.current; if (!s) return;
      const rect = cv.getBoundingClientRect();
      const cx = rect.left + cv.clientWidth / 2;
      // mouse tracking horizontal control (subtle)
      if (e.clientX < cx - 30) { s.keys.left = true; s.keys.right = false; }
      else if (e.clientX > cx + 30) { s.keys.right = true; s.keys.left = false; }
      else { s.keys.left = false; s.keys.right = false; }
      if (showIntro) setShowIntro(false);
    };
    const onMouseLeave = () => {
      const s = stateRef.current; if (!s) return;
      s.keys.left = false; s.keys.right = false;
    };

    // Touch control: a finger drag anywhere on the canvas maps the puppet's
    // target X position to the touch X (relative to canvas width). The physics
    // pulls the puppet toward targetX every frame.
    const updateTouchTarget = (clientX) => {
      const s = stateRef.current; if (!s) return;
      const rect = cv.getBoundingClientRect();
      const localX = clientX - rect.left;
      // clamp to canvas
      const tx = Math.max(0, Math.min(cv.clientWidth, localX));
      // convert to canvas drawing coordinates (clientWidth maps to s.w 1:1)
      s.targetX = (tx / cv.clientWidth) * s.w;
    };
    const onTouchStart = (e) => {
      if (!e.touches.length) return;
      updateTouchTarget(e.touches[0].clientX);
      setShowIntro(false);
    };
    const onTouchMove = (e) => {
      if (!e.touches.length) return;
      updateTouchTarget(e.touches[0].clientX);
    };
    const onTouchEnd = () => {
      const s = stateRef.current; if (!s) return;
      s.targetX = null;
    };
    cv.addEventListener("mousemove", onMouseMove);
    cv.addEventListener("mouseleave", onMouseLeave);
    cv.addEventListener("touchstart", onTouchStart, { passive: true });
    cv.addEventListener("touchmove", onTouchMove, { passive: true });
    cv.addEventListener("touchend", onTouchEnd);

    const assets = assetsRef.current;

    // Draw the moon-sky background as a single cover-fit image with very
    // slow parallax drift. No tiling — the moon is one focal point.
    const drawSky = (g, s) => {
      const sky = assets.sky;
      g.fillStyle = "#020203";
      g.fillRect(0, 0, s.w, s.h);
      if (!sky || !sky.width) return;

      const imgAspect = sky.width / sky.height;
      const viewAspect = s.w / s.h;
      let dw, dh;
      if (viewAspect > imgAspect) {
        dw = s.w * 1.05;
        dh = dw / imgAspect;
      } else {
        dh = s.h * 1.05;
        dw = dh * imgAspect;
      }
      // Very gentle parallax: moon drifts up slightly the further you fall,
      // but never repeats — clamp so it never reveals the edge.
      const maxDrift = Math.min(dh - s.h, dw * 0.05);
      const drift = Math.min(maxDrift, s.bgOffset * 0.03);
      const dx = (s.w - dw) / 2;
      const dy = (s.h - dh) / 2 - drift;
      g.drawImage(sky, dx, dy, dw, dh);

      // Soft additional vignette to blend edges with the black
      const vg = g.createRadialGradient(
        s.w/2, s.h*0.55, Math.min(s.w,s.h)*0.25,
        s.w/2, s.h*0.55, Math.max(s.w,s.h)*0.9
      );
      vg.addColorStop(0, "rgba(0,0,0,0)");
      vg.addColorStop(1, "rgba(0,0,0,0.55)");
      g.fillStyle = vg;
      g.fillRect(0,0,s.w,s.h);
    };

    const drawDistantClouds = (g, s) => {
      const cd = assets.cloud;
      if (!cd || !cd.width) return;
      for (const c of s.distantClouds) {
        c.x += c.vx;
        if (c.x < -c.w) c.x = s.w + 40;
        if (c.x > s.w + 40) c.x = -c.w;
        const py = c.y - (s.bgOffset * c.depth) % (s.h * 1.6);
        const yy = ((py % (s.h * 1.6)) + s.h * 1.6) % (s.h * 1.6) - s.h * 0.3;
        const aspect = cd.height / cd.width;
        g.save();
        g.globalAlpha = c.alpha;
        // tint darker for distance
        g.filter = "brightness(0.55) blur(2px)";
        g.drawImage(cd, c.x, yy, c.w, c.w * aspect);
        g.filter = "none";
        g.restore();
      }
    };

    const drawPlatform = (g, p) => {
      const cp = assets.cloud;
      if (!cp || !cp.width) {
        g.fillStyle = "rgba(230,230,230,0.9)";
        g.fillRect(p.x, p.y, p.w, p.h);
        return;
      }
      // The cloud sprite is roughly square — the landing top is around 30% from the top.
      // Draw it scaled so the visible top "ledge" sits on (p.x..p.x+p.w, p.y).
      const ratio = cp.height / cp.width;
      // Make the sprite wider than the logical platform so it overflows nicely.
      const sw = p.w * 1.5;
      const sh = sw * ratio;
      const sx = p.x + p.w / 2 - sw / 2;
      // Top of the cloud sprite is the landing surface — shift up by ~12% so the
      // ledge aligns with p.y.
      const sy = p.y - sh * 0.18;
      // Slight drop shadow under the cloud for depth
      g.save();
      g.shadowColor = "rgba(0,0,0,0.6)";
      g.shadowBlur = 18;
      g.shadowOffsetY = 10;
      g.drawImage(cp, sx, sy, sw, sh);
      g.restore();
    };

    const drawPlayer = (g, s) => {
      // Choose sprite based on horizontal velocity
      const vx = s.player.vx;
      let m = assets.mascot;
      if (vx < -0.6 && assets.mascotL && assets.mascotL.width) m = assets.mascotL;
      else if (vx > 0.6 && assets.mascotR && assets.mascotR.width) m = assets.mascotR;
      g.save();
      g.translate(s.player.x + s.player.w / 2, s.player.y + s.player.h / 2);
      // Lean tilt scales with velocity, but cap it
      const tilt = Math.max(-0.18, Math.min(0.18, vx * 0.03));
      g.rotate(tilt);
      g.shadowColor = "rgba(0,0,0,0.65)";
      g.shadowBlur = 22;
      g.shadowOffsetY = 12;
      if (m && m.width) {
        const aspect = m.height / m.width;
        const pw = s.player.w * 1.55;
        const ph = pw * aspect;
        g.drawImage(m, -pw / 2, -ph / 2, pw, ph);
      } else {
        g.fillStyle = "#cfcfcf";
        g.fillRect(-s.player.w / 2, -s.player.h / 2, s.player.w, s.player.h);
      }
      g.restore();
    };

    const loop = () => {
      const s = stateRef.current;
      if (!s) { rafRef.current = requestAnimationFrame(loop); return; }
      const { w, h } = s;
      s.time += 1 / 60;

      drawSky(ctx, s);
      drawDistantClouds(ctx, s);

      // ----- physics -----
      if (!s.dead) {
        // Horizontal control: keyboard keys OR touch target position
        if (s.targetX != null) {
          // tween player center toward target X
          const center = s.player.x + s.player.w / 2;
          const delta = s.targetX - center;
          // Apply proportional acceleration with damping
          s.player.vx += Math.max(-s.maxVx, Math.min(s.maxVx, delta * 0.18));
          s.player.vx *= 0.78;       // smoothing toward target
        } else {
          if (s.keys.left) s.player.vx -= s.moveAccel;
          if (s.keys.right) s.player.vx += s.moveAccel;
          if (!s.keys.left && !s.keys.right) s.player.vx *= s.friction;
        }
        s.player.vx = Math.max(-s.maxVx, Math.min(s.maxVx, s.player.vx));

        s.player.x += s.player.vx;
        if (s.player.x + s.player.w < 0) s.player.x = w;
        if (s.player.x > w) s.player.x = -s.player.w;

        s.player.vy += s.gravity;
        s.player.y += s.player.vy;
        s.player.tilt = s.player.vx * 0.04;

        if (s.player.vy > 0) {
          for (const p of s.platforms) {
            if (
              s.player.x + s.player.w > p.x + 10 &&
              s.player.x < p.x + p.w - 10 &&
              s.player.y + s.player.h > p.y &&
              s.player.y + s.player.h < p.y + p.h + 16
            ) {
              s.player.vy = s.jumpV;
              if (!s.hasLanded) s.hasLanded = true;
              // Only count NEW clouds (don't re-score the same one if landed on twice in a row)
              if (p.id !== s.lastPlatformId) {
                s.score += 1;
                s.lastPlatformId = p.id;
              }
              break;
            }
          }
        }

        for (const p of s.platforms) {
          p.x += p.drift;
          if (p.x < -p.w) p.x = w;
          if (p.x > w) p.x = -p.w;
        }

        if (s.player.y < h * 0.42) {
          const dy = h * 0.42 - s.player.y;
          s.player.y = h * 0.42;
          s.cameraY += dy;
          s.bgOffset += dy;
          for (const p of s.platforms) p.y += dy;
          for (const p of s.platforms) {
            if (p.y > h + 40) {
              // Progressive difficulty curve based on cloud count
              const t = Math.min(1, s.score / 80);
              const scale = s.isMobile ? 0.55 : 1.0;
              const minW = (130 - t * 60) * scale;
              const maxW = (200 - t * 90) * scale;
              const pw = minW + Math.random() * (maxW - minW);
              const verticalGap = h + 140 + t * 90;
              const driftMag = 0.55 + t * 1.2;
              p.y -= verticalGap;
              p.w = pw;
              p.x = Math.random() * (w - pw - 20) + 10;
              p.drift = (Math.random() - 0.5) * driftMag;
              p.scale = 0.9 + Math.random() * 0.3;
              p.id = s.platformIdCounter++;
            }
          }
        }

        if (s.player.y > h + 80) {
          s.dead = true;
          setGameOver(true);
          setScore(s.score);
          // Optimistic local best — overwritten by the server's authoritative
          // personal best as soon as the POST returns.
          const localBest = Math.max(s.score, parseInt(localStorage.getItem("nuvola_best") || "0", 10));
          localStorage.setItem("nuvola_best", String(localBest));
          setBestScore(localBest);
          if (apiBase && email) {
            axios
              .post(`${apiBase}/scores`, { score: s.score, email })
              .then((res) => {
                const data = res && res.data ? res.data : {};
                if (typeof data.personal_best === "number") {
                  setBestScore(data.personal_best);
                  localStorage.setItem("nuvola_best", String(data.personal_best));
                }
                if (typeof data.world_best === "number") {
                  setWorldBest(data.world_best);
                }
              })
              .catch(() => { /* keep optimistic local best */ });
          }
        }
      }

      // ----- draw foreground platforms (sorted near→far for depth) -----
      const sorted = [...s.platforms].sort((a,b) => a.y - b.y);
      for (const p of sorted) drawPlatform(ctx, p);

      drawPlayer(ctx, s);

      // Top vignette
      const topGrd = ctx.createLinearGradient(0, 0, 0, 140);
      topGrd.addColorStop(0, "rgba(0,0,0,0.55)");
      topGrd.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = topGrd;
      ctx.fillRect(0, 0, w, 140);
      // Bottom vignette
      const botGrd = ctx.createLinearGradient(0, h - 140, 0, h);
      botGrd.addColorStop(0, "rgba(0,0,0,0)");
      botGrd.addColorStop(1, "rgba(0,0,0,0.6)");
      ctx.fillStyle = botGrd;
      ctx.fillRect(0, h - 140, w, 140);

      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
      window.removeEventListener("keydown", onDown);
      window.removeEventListener("keyup", onUp);
      cv.removeEventListener("mousemove", onMouseMove);
      cv.removeEventListener("mouseleave", onMouseLeave);
      cv.removeEventListener("touchstart", onTouchStart);
      cv.removeEventListener("touchmove", onTouchMove);
      cv.removeEventListener("touchend", onTouchEnd);
    };
  }, [initState, apiBase, email, assetsReady, showIntro]);

  useEffect(() => {
    const t = setInterval(() => {
      const s = stateRef.current;
      if (s && !s.dead) setScore(s.score);
    }, 180);
    return () => clearInterval(t);
  }, []);

  const restart = () => {
    const cv = canvasRef.current;
    if (!cv) return;
    stateRef.current = initState(cv.clientWidth, cv.clientHeight);
    setGameOver(false);
    setScore(0);
    setShowIntro(true);
    setShowShare(false);
  };

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 bg-black overflow-hidden"
      data-testid="game-stage"
    >
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full block" data-testid="game-canvas" />

      {/* Top HUD: logo + score + audio */}
      <div className="absolute top-0 inset-x-0 z-30 flex items-center justify-between px-4 sm:px-10 py-4 sm:py-5 text-white/80 pointer-events-none">
        <div className="flex items-center pointer-events-auto">
          <img src="/assets/nuvola_w.png" alt="nuvola" className="h-5 sm:h-6 w-auto opacity-90" />
        </div>

        <div className="font-mono text-[10px] sm:text-[11px] tracking-[0.25em] sm:tracking-[0.3em] uppercase text-white/70" data-testid="score-display">
          <span className="opacity-50 mr-2 sm:mr-3 hidden sm:inline">clouds</span>
          <span className="text-white text-base sm:text-lg tracking-tight">{String(score).padStart(5, "0")}</span>
          <span className="opacity-50 ml-3 sm:ml-4 mr-2 hidden sm:inline">best</span>
          <span className="text-white/70 tracking-tight ml-2 sm:ml-0">{String(bestScore).padStart(5, "0")}</span>
        </div>

        <button
          onClick={toggleAudio}
          className="opacity-60 hover:opacity-100 transition-opacity p-2 -m-2 pointer-events-auto"
          data-testid="audio-toggle-game"
          aria-label="toggle ambient audio"
        >
          {audioOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
        </button>
      </div>

      {/* Loading state */}
      {!assetsReady && (
        <div className="absolute inset-0 z-20 flex items-center justify-center text-white/50 font-mono text-[10px] tracking-[0.4em] uppercase" data-testid="game-loading">
          loading the sky…
        </div>
      )}

      {showIntro && assetsReady && !gameOver && (
        <div
          className="absolute inset-0 z-20 flex items-end justify-center pb-16 sm:pb-20 pointer-events-none"
          data-testid="game-intro"
        >
          <div className="text-center backdrop-blur-md bg-black/30 px-6 sm:px-10 py-4 sm:py-5 border border-white/10 rounded-sm">
            <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-white/70 hidden sm:block">
              ← →  ·  a / d  ·  move
            </p>
            <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-white/70 sm:hidden">
              slide finger left / right
            </p>
          </div>
        </div>
      )}

      {gameOver && (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/70 backdrop-blur-md" data-testid="game-over">
          <div className="text-center w-full max-w-md px-8">
            <p className="font-mono text-[10px] tracking-[0.45em] uppercase text-white/55 mb-6">
              you fell out of the sky
            </p>
            <h2 className="font-display italic text-white text-5xl sm:text-7xl mb-6 leading-none">
              {score}
            </h2>
            <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-white/45 mb-6 flex items-center justify-center gap-4">
              <span data-testid="best-score">your best · {bestScore}</span>
              <span className="text-white/25" aria-hidden="true">│</span>
              <span data-testid="world-best-score">worlds best · {worldBest}</span>
            </p>
            <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-white/70 mb-6">
              highest score wins a prize
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={restart}
                className="inline-flex items-center justify-center gap-3 px-4 py-3 border border-white/70 text-white font-mono text-[11px] tracking-[0.4em] uppercase whitespace-nowrap hover:bg-white hover:text-black transition-colors duration-500"
                data-testid="restart-button"
              >
                <RotateCw size={14} />
                <span>fall again</span>
              </button>
              <button
                onClick={() => setShowShare(true)}
                className="inline-flex items-center justify-center gap-3 px-4 py-3 bg-white text-black font-mono text-[11px] tracking-[0.4em] uppercase whitespace-nowrap hover:bg-white/85 transition-colors duration-500"
                data-testid="share-open"
              >
                <Share2 size={14} />
                <span>share</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {showShare && (
        <ShareCard
          score={score}
          onClose={() => setShowShare(false)}
          onRestart={() => { setShowShare(false); restart(); }}
          onExitToLanding={onExitToLanding}
        />
      )}
    </div>
  );
}
